import api from './api';
import { buildQueryParams } from './utils/query-params';
import type {
  ApiResponse,
  PaginatedResponse,
  Invoice,
  InvoiceFilters,
  CreateInvoiceData,
  UpdateInvoiceData,
  BusinessCategory,
  SendInvoiceEmailPayload,
  SendInvoiceWhatsAppPayload,
  SendEmailPreview,
  SendWhatsAppPreview,
} from '@/types';

// Re-export BusinessCategory for consumers that import types alongside the
// invoices API client. Each Invoice / CreateInvoiceData / UpdateInvoiceData
// shape in @/types carries a `business_category` field (nullable on read,
// optional on create/update).
export type { BusinessCategory };

export const getInvoices = async (filters: InvoiceFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<PaginatedResponse<Invoice>>(`/invoices?${query}`);
  return response.data;
};

/**
 * Invoices already generated from a given quote (newest first, capped at 5).
 *
 * Backs the composer's "an invoice already exists for this quote" banner.
 * Callers MUST tolerate a 403 — a user can hold `invoices.create` without
 * `invoices.view` — and treat that case as "unknown" rather than "none".
 * The banner is a best-effort hint, not a guarantee: there is deliberately
 * no unique constraint on `invoices.quote_id`, so re-invoicing a quote is
 * allowed and this check only warns.
 */
export const getInvoicesForQuote = async (quoteId: string): Promise<Invoice[]> => {
  const query = buildQueryParams({
    quote_id: quoteId,
    per_page: 5,
    sort: 'created_at',
    direction: 'desc',
  });
  const response = await api.get<PaginatedResponse<Invoice>>(`/invoices?${query}`);
  return response.data.data;
};

/**
 * Per-role dashboard — finance rollup consumed by the accountant/admin
 * dashboards (invoice-status donut, revenue-trend area, outstanding/overdue
 * stat tiles). Mirrors `GET /invoices/stats`.
 *
 * NOTE ON THE STATUS VOCABULARY: by_status uses the app's full derived-status
 * vocabulary ({@link import('@/types').InvoiceStatus}: `draft | sent | partial
 * | paid | void` post-Phase-64), matching the invoice list's chips 1:1.
 * Consumers should still iterate `by_status` defensively (Object.entries +
 * fallback label) so older/newer endpoint vocabularies keep rendering.
 */
export interface InvoiceStats {
  by_status: Record<'draft' | 'sent' | 'partial' | 'paid' | 'void', { count: number; total: number }>;
  overdue: { count: number; total: number };
  outstanding_total: number;
  paid_this_month: number;
  revenue_by_month: { month: string; total: number }[];
}

/**
 * GET /invoices/stats — clinic-wide invoice rollup for the dashboard.
 * Server-side scoped by the caller's visibility + `invoices.view`; callers
 * should still gate the fetch on `invoices.view` client-side so the widget is
 * omitted (not errored) for roles without the grant.
 */
export const getInvoiceStats = async (): Promise<InvoiceStats> => {
  const response = await api.get<InvoiceStats>('/invoices/stats');
  return response.data;
};

/**
 * Small "latest invoices" slice for dashboard rails — page 1, newest first.
 * Thin wrapper over {@link getInvoices}; returns the raw paginated envelope so
 * callers can read `meta.total` alongside the rows.
 */
export const getRecentInvoices = async (limit = 5) => {
  return getInvoices({
    page: 1,
    per_page: limit,
    sort: 'created_at',
    direction: 'desc',
  });
};

export const getInvoice = async (id: string) => {
  const response = await api.get<ApiResponse<Invoice>>(`/invoices/${id}`);
  return response.data.data;
};

export const createInvoice = async (data: CreateInvoiceData) => {
  const response = await api.post<ApiResponse<Invoice>>('/invoices', data);
  return response.data.data;
};

// ArtDent 260918 — an edit to an ISSUED invoice can move money: a total edited
// below what was paid releases the excess to the patient's credit, and the
// backend says how much in `meta.released_to_deposit` so the sheet can name it.
export const updateInvoice = async (id: string, data: UpdateInvoiceData) => {
  const response = await api.put<
    ApiResponse<Invoice> & { meta?: { released_to_deposit?: number } }
  >(`/invoices/${id}`, data);
  return {
    invoice: response.data.data,
    releasedToDeposit: Number(response.data.meta?.released_to_deposit ?? 0),
  };
};

export const deleteInvoice = async (id: string) => {
  await api.delete(`/invoices/${id}`);
};

// ArtDent 260918 — put a soft-deleted invoice back, exactly as it was.
export const restoreInvoice = async (id: string) => {
  const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/restore`);
  return response.data.data;
};

export const issueInvoice = async (id: string) => {
  const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/issue`);
  return response.data.data;
};

/**
 * Report an already-issued invoice to the tax authority.
 *
 * Separate from issueInvoice on purpose: issuing no longer fiscalizes, so this
 * is the operator's explicit act. A 2xx does NOT mean the report succeeded —
 * an upstream refusal comes back as `fiscal_status: "failed"` with
 * `fiscal_error` on the returned invoice, which the caller must surface.
 */
export const fiscalizeInvoice = async (id: string, exchangeRate?: number) => {
  const response = await api.post<ApiResponse<Invoice>>(
    `/invoices/${id}/fiscalize`,
    // Only sent when the invoice is missing one — the composer no longer
    // requires it, and an issued invoice can no longer be edited, so the
    // report call is where a foreign-currency rate is finally supplied.
    exchangeRate !== undefined ? { fiscal_exchange_rate: exchangeRate } : {},
  );
  return response.data.data;
};

// Phase 64 D-03 — explicit draft → sent transition. Backend's
// `InvoiceController::markAsSent` (NOT gated by feature:payments_enabled —
// the mark-as-sent surface must work on clinics without the payments
// kill-switch flipped on). Permission gate: `invoices.issue` via the
// existing InvoicePolicy::issue check.
export const markAsSentInvoice = async (id: string) => {
  const response = await api.post<ApiResponse<Invoice>>(
    `/invoices/${id}/mark-as-sent`,
  );
  return response.data.data;
};

export const voidInvoice = async (id: string) => {
  const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/void`);
  return response.data.data;
};

export const duplicateInvoice = async (id: string) => {
  const response = await api.post<ApiResponse<Invoice>>(`/invoices/${id}/duplicate`);
  return response.data.data;
};

// Phase 66 D-12 / D-16 / D-23 — PDF download + email/WhatsApp send.

/**
 * Build the URL the browser opens in a new tab for PDF download.
 *
 * Sanctum SPA auth uses cookies (withCredentials: true on the axios
 * instance), so window.open carries the session cookie automatically —
 * no need for blob/Authorization-header hacks. The backend returns
 * application/pdf with Content-Disposition: attachment.
 *
 * The resulting URL embeds the api base + /invoices/{id}/pdf path.
 */
/**
 * Streams the invoice PDF through the authed axios instance and opens it in a
 * new tab. Mirrors openStatementPdf / downloadWalkoutReceipt.
 *
 * MUST NOT be replaced by window.open on the raw endpoint URL: this app
 * authenticates with a Bearer token, so a plain browser navigation carries no
 * Authorization header. Sanctum rejects it, and Laravel then tries to redirect
 * the (text/html) request to a `login` route that does not exist in an
 * API-only app — surfacing as a 500 RouteNotFoundException rather than a 401.
 */
export const openInvoicePdf = async (id: string): Promise<void> => {
  const response = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });

  const url = window.URL.createObjectURL(
    new Blob([response.data], { type: 'application/pdf' }),
  );
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

/**
 * Server-resolved template defaults for the Send Email dialog.
 * Tokens ({invoiceNumber}, {leadFirstName}, {clinicName}, {paymentBlock})
 * are substituted server-side; the operator sees fully-resolved text.
 */
export const previewInvoiceEmail = async (id: string): Promise<SendEmailPreview> => {
  const response = await api.get<SendEmailPreview>(`/invoices/${id}/send-email/preview`);
  return response.data;
};

/**
 * Server-resolved template defaults for the Send WhatsApp dialog.
 * Body contains literal {pdfUrl} placeholder — the signed URL is only
 * resolved at dispatch time (Phase 66 D-22).
 */
export const previewInvoiceWhatsApp = async (id: string): Promise<SendWhatsAppPreview> => {
  const response = await api.get<SendWhatsAppPreview>(`/invoices/${id}/send-whatsapp/preview`);
  return response.data;
};

/**
 * Queue the email send. Returns 202 with { queued: true }. The Mailable
 * runs asynchronously on the queue worker; failure surfaces in Laravel's
 * failed_jobs table — frontend has no real-time visibility on email
 * delivery (no Phase 66 polling for email; only WhatsApp polls).
 */
export const sendInvoiceEmail = async (
  id: string,
  payload: SendInvoiceEmailPayload,
): Promise<{ queued: true }> => {
  const response = await api.post<{ queued: true }>(`/invoices/${id}/send-email`, payload);
  return response.data;
};

/**
 * Queue the WhatsApp send. Returns 202 with { queued: true,
 * whatsapp_message_id }. The dialog uses whatsapp_message_id to poll
 * /whatsapp/messages/{id} for 30s post-dispatch (Phase 66 D-25).
 */
export const sendInvoiceWhatsApp = async (
  id: string,
  payload: SendInvoiceWhatsAppPayload,
): Promise<{ queued: true; whatsapp_message_id: string }> => {
  const response = await api.post<{ queued: true; whatsapp_message_id: string }>(
    `/invoices/${id}/send-whatsapp`,
    payload,
  );
  return response.data;
};
